import { useMemo, useState } from "react";
import { Alert, Button, Table } from "antd";
import { ExpandAltOutlined, ShrinkOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Page } from "shared/ui";
import { useGetMyTaskExecutionsQuery } from "shared/api";
import {
  buildMyTaskExecutionRows,
  collectMyTaskExecutionSectionKeys,
} from "shared/helpers";
import { useCompleteMyTasksTreeColumns } from "shared/hooks";
import { MyTaskExecutionRow } from "shared/types";
import { CompleteTaskModal } from "./CompleteTaskModal";

const CompleteMyTasks = () => {
  const { t } = useTranslation();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isLoading, isFetching, isError, refetch } =
    useGetMyTaskExecutionsQuery();

  const onCompleteTask = (taskExecutionId: number) => {
    setSelectedId(taskExecutionId);
    setIsModalOpen(true);
  };

  const columns = useCompleteMyTasksTreeColumns({ onCompleteTask });

  const rows = useMemo<MyTaskExecutionRow[]>(() => {
    const root = data?.my_task_executions;
    return root ? buildMyTaskExecutionRows(root) : [];
  }, [data]);

  // Keep the tree fully expanded by default (so every task is visible) while
  // still letting the user collapse individual sections — mirrors the Task Hub
  // settings tree.
  const allSectionKeys = useMemo(
    () => collectMyTaskExecutionSectionKeys(rows),
    [rows]
  );
  const [collapsedKeys, setCollapsedKeys] = useState<string[]>([]);
  const expandedRowKeys = allSectionKeys.filter(
    (key) => !collapsedKeys.includes(key)
  );

  const allCollapsed =
    allSectionKeys.length > 0 && expandedRowKeys.length === 0;

  const toggleExpandAll = () =>
    setCollapsedKeys(allCollapsed ? [] : allSectionKeys);

  return (
    <Page
      title={t("taskHub.myTasks.completeMyTasksTitle") || "Complete my tasks"}
    >
      {isError ? (
        <Alert
          className="mt-4"
          type="error"
          showIcon
          message={
            t("taskHub.myTasks.loadError") || "Failed to load your tasks"
          }
          description={
            t("taskHub.myTasks.loadErrorDescription") ||
            "Something went wrong while loading your task executions."
          }
          action={
            <Button size="small" danger onClick={() => refetch()}>
              {t("Retry") || "Retry"}
            </Button>
          }
        />
      ) : (
        <>
          {allSectionKeys.length > 0 && (
            <div className="flex items-center gap-2 mt-4 mb-2">
              <Button
                className="!inline-flex !items-center"
                icon={allCollapsed ? <ExpandAltOutlined /> : <ShrinkOutlined />}
                onClick={toggleExpandAll}
              >
                {allCollapsed
                  ? t("taskHub.sections.expandAll") || "Expand all"
                  : t("taskHub.sections.collapseAll") || "Collapse all"}
              </Button>
            </div>
          )}

          <Table<MyTaskExecutionRow>
            className="thub-table mt-2"
            columns={columns}
            dataSource={rows}
            rowKey="key"
            loading={isLoading || isFetching}
            pagination={false}
            scroll={{ x: true }}
            rowClassName={(record) =>
              record.rowType === "section" ? "thub-section-row" : ""
            }
            expandable={{
              expandedRowKeys,
              onExpand: (expanded, record) =>
                setCollapsedKeys((prev) =>
                  expanded
                    ? prev.filter((key) => key !== record.key)
                    : [...prev, record.key]
                ),
            }}
          />
        </>
      )}

      <CompleteTaskModal
        open={isModalOpen}
        taskExecutionId={selectedId}
        onClose={() => setIsModalOpen(false)}
      />
    </Page>
  );
};

export { CompleteMyTasks };
